import { useState, useEffect, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntranetAccess } from '../hooks/useIntranetAccess'
import { logout } from '../lib/phpAuth'
import { getSettings } from '../lib/apiPhpAdapter'
import { parseModuleDevFlags, userBypassesModuleDev, type ModuleDevFlags } from '../lib/moduleDevAccess'
import { DOVOLENA_MODULE_DESCRIPTION, DOVOLENA_MODULE_TITLE } from '../lib/dovolenaRequestType'
import { Header } from '../components/Header'
import WeeklyNews from '../components/WeeklyNews'
import { useAlert } from '../contexts/AlertContext'
import { DetailsModal } from '../components/DetailsModal'
import MicrosoftCopilotLauncher from '../components/MicrosoftCopilotLauncher'
import './IntranetHome.css'

function IntranetHome() {
  // ВАЖНО: Все хуки должны быть вызваны ДО любого условного рендеринга
  const navigate = useNavigate()
  const { user, accessGranted, loading } = useIntranetAccess()
  const { showAlert } = useAlert()
  const [showTicketHelp, setShowTicketHelp] = useState(false)
  const [showPurchaseHelp, setShowPurchaseHelp] = useState(false)
  const [showPropertyHelp, setShowPropertyHelp] = useState(false)
  const [moduleDevFlags, setModuleDevFlags] = useState<ModuleDevFlags | null>(null)

  useEffect(() => {
    if (!accessGranted || !user) return
    let cancelled = false
    getSettings()
      .then((s) => {
        if (!cancelled) setModuleDevFlags(parseModuleDevFlags(s))
      })
      .catch(() => {
        if (!cancelled) {
          setModuleDevFlags({ fksp: true, dovolena: true, travel_orders: true })
        }
      })
    return () => {
      cancelled = true
    }
  }, [accessGranted, user])

  const handleLogout = async () => {
    try {
      // Выход через PHP API
      await logout()
      // Перенаправляем на главную страницу
      // App.tsx автоматически покажет страницу входа, когда authenticated станет false
      navigate('/', { replace: true })
    } catch (error) {
      console.error('Logout error:', error)
      // В случае ошибки все равно перенаправляем и удаляем токен
      navigate('/', { replace: true })
    }
  }

  // Показываем загрузку во время проверки (не показываем сообщение об отказе сразу)
  // Используем короткую задержку, чтобы избежать мигания при быстрых переходах
  if (loading || accessGranted === null) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ 
          fontSize: '1.1rem', 
          color: '#666',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #14b8a6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div>Načítání...</div>
        </div>
      </div>
    )
  }

  // Если доступ не разрешен - всегда показываем загрузку вместо ошибки
  // Это помогает, если пользователь еще синхронизируется
  // Никогда не показываем ошибку, всегда даем шанс получить доступ
  if (accessGranted === false || (accessGranted === null && !loading)) {
    // Продолжаем показывать загрузку, если доступ еще не разрешен
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ 
          fontSize: '1.1rem', 
          color: '#666',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #14b8a6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div>Načítání...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="intranet-home">
      <Header
        leftButtons={
          <>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Školní intranet</h1>
            {user && (
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                {user.name || user.email}
              </span>
            )}
          </>
        }
        rightContent={
          <button
            onClick={handleLogout}
            className="logout-button"
          >
            Odhlásit se
          </button>
        }
        showLogo={true}
      />
      <div className="intranet-container">
        <div className="intranet-content">
          <h2>Vítejte v interním systému školy</h2>
          <WeeklyNews />
          <p className="subtitle">Vyberte systém, ke kterému chcete přistupovat</p>

          <div className="systems-grid">
            <div
              onClick={() => navigate('/my-cabinet')}
              className="system-card-link"
              style={{ cursor: 'pointer' }}
            >
              <div className="system-card" style={{ boxShadow: '0 0 0 2px rgba(13, 148, 136, 0.35)' }}>
                <div className="system-icon">👤</div>
                <h3>Můj kabinet</h3>
                <p>Údaje o vás a odkazy ke všem systémům podle oprávnění</p>
                <button
                  type="button"
                  className="system-button"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate('/my-cabinet')
                  }}
                >
                  Otevřít
                </button>
              </div>
            </div>

            <div 
              className="system-card ticket-system"
              style={{ position: 'relative' }}
              onClick={() => navigate('/app/select-role')}
            >
              <button
                className="help-button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowTicketHelp(true)
                }}
                title="Jak použít"
                aria-label="Zobrazit nápovědu"
              >
                Jak použít
              </button>
              <div className="system-icon">🎫</div>
              <h3>Závady a požadavky</h3>
              <p>Správa budov + IT</p>
              <button className="system-button" onClick={(e) => {
                e.stopPropagation()
                navigate('/app/select-role')
              }}>Otevřít</button>
            </div>

            <div 
              className="system-card"
              style={{ position: 'relative' }}
              onClick={() => navigate('/purchase-requests/select-role')}
            >
              <button
                className="help-button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowPurchaseHelp(true)
                }}
                title="Jak použít"
              >
                Jak použít
              </button>
              <div className="system-icon">🛒</div>
              <h3>Žádanky o nákup</h3>
              <p>Správa žádostí o nákup</p>
              <button className="system-button" onClick={(e) => {
                e.stopPropagation()
                navigate('/purchase-requests/select-role')
              }}>Otevřít</button>
            </div>

            <div 
              className="system-card"
              style={{ position: 'relative' }}
              onClick={() => navigate('/property/select-role')}
            >
              <button
                className="help-button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowPropertyHelp(true)
                }}
                title="Jak použít"
              >
                Jak použít
              </button>
              <div className="system-icon">🏢</div>
              <h3>Správy majetku</h3>
              <p>Správa a evidence majetku</p>
              <button className="system-button" onClick={(e) => {
                e.stopPropagation()
                navigate('/property/select-role')
              }}>Otevřít</button>
            </div>

            <div 
              onClick={() => navigate('/public-canteen')}
              className="system-card-link"
              style={{ cursor: 'pointer' }}
            >
              <div className="system-card">
                <div className="system-icon">🍽️</div>
                <h3>Školní Jídelna</h3>
                <p>Hodnocení kvality jídel a obsluhy</p>
                <button className="system-button" onClick={(e) => {
                  e.stopPropagation()
                  navigate('/public-canteen')
                }}>Otevřít</button>
              </div>
            </div>

            <div 
              onClick={() => navigate('/calendar')}
              className="system-card-link"
              style={{ cursor: 'pointer' }}
            >
              <div className="system-card">
                <div className="system-icon">📅</div>
                <h3>Kalendář akcí školy</h3>
                <p>Školní akce a programy</p>
                <button className="system-button" onClick={(e) => {
                  e.stopPropagation()
                  navigate('/calendar')
                }}>Otevřít</button>
              </div>
            </div>

            <div 
              onClick={() => navigate('/calendar-events')}
              className="system-card-link"
              style={{ cursor: 'pointer' }}
            >
              <div className="system-card">
                <div className="system-icon">🏫</div>
                <h3>Kalendář využití školních prostor</h3>
                <p>Rezervace tělocvičen, aul a učeben</p>
                <button className="system-button" onClick={(e) => {
                  e.stopPropagation()
                  navigate('/calendar-events')
                }}>Otevřít</button>
              </div>
            </div>

            <a 
              href="https://isscheb-my.sharepoint.com/:f:/g/personal/vasyliev_iss-cheb_cz/IgDIFdap3A8WT4FsJTx4D_GMATTZ5QdSq1l9fM-tj5De0Q8?e=A31iu0"
              target="_blank"
              rel="noopener noreferrer"
              className="system-card-link"
            >
              <div className="system-card">
                <div className="system-icon">📁</div>
                <h3>Školní dokumenty</h3>
                <p>Dokumenty a sdílené soubory</p>
                <button className="system-button">Otevřít</button>
              </div>
            </a>

            <div 
              onClick={() => navigate('/school-employees')}
              className="system-card-link"
              style={{ cursor: 'pointer' }}
            >
              <div className="system-card">
                <div className="system-icon">👥</div>
                <h3>Seznam zaměstnanců</h3>
                <p>Kontakty a telefony zaměstnanců školy</p>
                <button className="system-button">Otevřít</button>
              </div>
            </div>


            <div 
              onClick={() => navigate('/it-admin-info')}
              className="system-card-link"
              style={{ cursor: 'pointer' }}
            >
              <div className="system-card">
                <div className="system-icon">💻</div>
                <h3>Informace od IT</h3>
                <p>Důležité informace od IT administrátora</p>
                <button className="system-button">Otevřít</button>
              </div>
            </div>


            {/* Moduly s volitelným režimem vývoje (řídí správce IT v administraci) */}
            {(() => {
              const devFksp = moduleDevFlags?.fksp ?? true
              const devDovolena = moduleDevFlags?.dovolena ?? true
              const devTravel = moduleDevFlags?.travel_orders ?? true

              const canFksp =
                !devFksp || (user != null && userBypassesModuleDev('fksp', user))
              const canDovolena =
                !devDovolena || (user != null && userBypassesModuleDev('dovolena', user))
              const canTravel =
                !devTravel || (user != null && userBypassesModuleDev('travel_orders', user))

              const bannerStyle: CSSProperties = {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40px',
                background:
                  'repeating-linear-gradient(45deg, #ffc107, #ffc107 10px, #ff9800 10px, #ff9800 20px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }

              return (
                <>
                  {/* Žádosti FKSP */}
                  <div
                    className="system-card-link"
                    style={{
                      cursor: canFksp ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => {
                      if (canFksp) {
                        navigate('/fksp/select-role')
                      } else {
                        showAlert(
                          'Modul je uzavřen. Přístup mají ředitel, správce IT a hlavní ekonom.',
                          'warning'
                        )
                      }
                    }}
                  >
                    <div
                      className={`system-card${devFksp ? ' system-card--dev-banner' : ''}`}
                      style={{ position: 'relative', opacity: canFksp ? 1 : 0.7 }}
                    >
                      {devFksp && (
                        <div style={bannerStyle}>
                          <span
                            style={{
                              color: '#000',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                              letterSpacing: '2px',
                            }}
                          >
                            ⚠️ VÝVOJ
                          </span>
                        </div>
                      )}
                      <div className="system-icon">🏦</div>
                      <h3>Žádosti FKSP</h3>
                      <p>Příspěvky z fondu FKSP</p>
                      <button className="system-button" disabled={!canFksp}>
                        {canFksp ? 'Otevřít' : 'Nedostupné'}
                      </button>
                    </div>
                  </div>

                  {/* Žádosti o nepřítomnost */}
                  <div
                    className="system-card-link"
                    style={{
                      cursor: canDovolena ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => {
                      if (canDovolena) {
                        navigate('/dovolena/select-role')
                      } else {
                        showAlert(
                          'Modul je uzavřen. Přístup mají ředitel, správce IT a hlavní ekonom.',
                          'warning'
                        )
                      }
                    }}
                  >
                    <div
                      className={`system-card${devDovolena ? ' system-card--dev-banner' : ''}`}
                      style={{ position: 'relative', opacity: canDovolena ? 1 : 0.7 }}
                    >
                      {devDovolena && (
                        <div style={bannerStyle}>
                          <span
                            style={{
                              color: '#000',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                              letterSpacing: '2px',
                            }}
                          >
                            ⚠️ VÝVOJ
                          </span>
                        </div>
                      )}
                      <div className="system-icon">🏖️</div>
                      <h3>{DOVOLENA_MODULE_TITLE}</h3>
                      <p>{DOVOLENA_MODULE_DESCRIPTION}</p>
                      <button className="system-button" disabled={!canDovolena}>
                        {canDovolena ? 'Otevřít' : 'Nedostupné'}
                      </button>
                    </div>
                  </div>

                  {/* Cestovní příkaz */}
                  <div
                    className="system-card-link"
                    style={{
                      cursor: canTravel ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => {
                      if (canTravel) {
                        navigate('/travel-orders')
                      } else {
                        showAlert(
                          'Modul je uzavřen. Přístup mají ředitel, zástupce ředitele, správce IT a hlavní ekonom.',
                          'warning'
                        )
                      }
                    }}
                  >
                    <div
                      className={`system-card${devTravel ? ' system-card--dev-banner' : ''}`}
                      style={{ position: 'relative', opacity: canTravel ? 1 : 0.7 }}
                    >
                      {devTravel && (
                        <div style={bannerStyle}>
                          <span
                            style={{
                              color: '#000',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                              letterSpacing: '2px',
                            }}
                          >
                            ⚠️ VÝVOJ
                          </span>
                        </div>
                      )}
                      <div className="system-icon">✈️</div>
                      <h3>Cestovní příkaz</h3>
                      <p>Správa cestovních příkazů</p>
                      <p
                        style={{
                          fontSize: '0.8rem',
                          lineHeight: 1.35,
                          color: 'rgba(0,0,0,0.75)',
                          margin: '0 0 0.5rem 0',
                          textAlign: 'left',
                        }}
                      >
                        Chcete-li náhradu výdajů na stravné, musí každý spolucestující vyplnit vlastní cestovní příkaz.
                      </p>
                      <button className="system-button" disabled={!canTravel}>
                        {canTravel ? 'Otevřít' : 'Nedostupné'}
                      </button>
                    </div>
                  </div>

                  {/* Docházkový systém — výběr role */}
                  <div
                    className="system-card-link"
                    style={{
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => navigate('/attendance')}
                  >
                    <div
                      className="system-card system-card--dev-banner"
                      style={{
                        position: 'relative',
                      }}
                    >
                      <div style={bannerStyle}>
                        <span
                          style={{
                            color: '#000',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            letterSpacing: '0.05em',
                          }}
                        >
                          VÝVOJ
                        </span>
                      </div>
                      <div className="system-icon">⏱️</div>
                      <h3>Docházkový systém</h3>
                      <p>Evidence docházky — uživatel, vedoucí, personalista</p>
                      <button className="system-button" type="button">
                        Otevřít
                      </button>
                    </div>
                  </div>

                  {/* Rezervace vozidel */}
                  <div
                    className="system-card-link"
                    style={{
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => navigate('/vehicle-fleet')}
                  >
                    <div
                      className="system-card system-card--dev-banner"
                      style={{ position: 'relative' }}
                    >
                      <div style={bannerStyle}>
                        <span
                          style={{
                            color: '#000',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            letterSpacing: '0.05em',
                          }}
                        >
                          VÝVOJ
                        </span>
                      </div>
                      <div className="system-icon">🚗</div>
                      <h3>Rezervace vozidel</h3>
                      <p>Systém rezervace školního autoprovozu pro řidiče školy</p>
                      <button className="system-button" type="button">
                        Otevřít
                      </button>
                    </div>
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      </div>
      <MicrosoftCopilotLauncher />
      
      <DetailsModal
        isOpen={showTicketHelp}
        onClose={() => setShowTicketHelp(false)}
        title="Jak použít systém pro Závady a požadavky"
      >
        <div style={{ lineHeight: '1.6' }}>
          <h3 style={{ marginTop: 0, color: '#0d9488' }}>Rychlý návod:</h3>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <strong>1. Vytvoření žádosti:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Klikněte na "Otevřít" a vyberte "Žadatel"</li>
              <li>Vyplňte formulář: správce, budova, popis problému, telefon</li>
              <li>Volitelně přiložte fotografii nebo dokument (max 10 MB)</li>
              <li>Klikněte "Vytvořit žádost"</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>2. Výběr správce:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li><strong>Správce IT</strong> - pro počítače, síť, tiskárny, software</li>
              <li><strong>Správce Budov</strong> - pro vytápění, osvětlení, voda, dveře, okna</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>3. Popis problému:</strong>
            <p style={{ margin: '0.5rem 0', padding: '0.75rem', background: '#f0f9ff', borderRadius: '6px', borderLeft: '3px solid #0d9488' }}>
              Uveďte <strong>přesné umístění</strong> (číslo místnosti, patro), 
              <strong> příznaky problému</strong> a <strong>kdy se objevil</strong>.
              Čím detailnější popis, tím rychlejší řešení!
            </p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>4. Sledování žádosti:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li><span style={{ color: '#3b82f6', fontWeight: 'bold' }}>Nový</span> - žádost byla vytvořena</li>
              <li><span style={{ color: '#f59e0b', fontWeight: 'bold' }}>V řešení</span> - správce pracuje na problému</li>
              <li><span style={{ color: '#10b981', fontWeight: 'bold' }}>Dokončeno</span> - problém byl vyřešen</li>
            </ul>
          </div>

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
            <strong>💡 Tip:</strong> Pokud žádost zůstává ve stavu "Nový" déle než týden, 
            kontaktujte správce přímo pomocí emailu uvedeného v detailu žádosti.
          </div>

          <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
            <strong>Potřebujete další pomoc?</strong><br/>
            Kontaktujte IT správce: <a href="mailto:vasyliev@iss-cheb.cz" style={{ color: '#0d9488' }}>vasyliev@iss-cheb.cz</a>
          </div>
        </div>
      </DetailsModal>

      <DetailsModal
        isOpen={showPurchaseHelp}
        onClose={() => setShowPurchaseHelp(false)}
        title="Jak použít systém žádanek o nákup"
      >
        <div style={{ lineHeight: '1.6' }}>
          <h3 style={{ marginTop: 0, color: '#0d9488' }}>Rychlý návod:</h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>1. Vytvoření žádosti (Žadatel):</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Klikněte na „Otevřít“ a vyberte kartu <strong>Žadatel</strong></li>
              <li>Klikněte na „+ Vytvořit novou žádost“</li>
              <li>Pokud nejste vedoucí, <strong>vyberte svého vedoucího</strong> (povinné)</li>
              <li>Pokud jste vedoucí, výběr není potřeba — žádost se schválí automaticky vámi</li>
              <li>Vyplňte: středisko, předmět, popis, důvod; volitelně dodavatele, částku a přílohu</li>
              <li>Klikněte „Vytvořit žádost“</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>2. Schválení vedoucím:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Vedoucí otevře kartu <strong>Vedoucí</strong> a uvidí žádosti, kde je zvolen jako schvalovatel</li>
              <li>Může žádost <strong>schválit</strong>, nebo <strong>zamítnout</strong> s uvedením důvodu</li>
              <li>Žadatel může žádost <strong>zrušit jen před schválením</strong> vedoucím</li>
              <li>Po schválení může žádost zrušit pouze vedoucí</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>3. Další průběh žádosti:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li><strong>Čeká na vedoucího</strong> — čeká na schválení vedoucím</li>
              <li><strong>Nová</strong> — schváleno vedoucím, u hlavního ekonoma</li>
              <li><strong>Schváleno ekonomem</strong> → žádost jde řediteli</li>
              <li><strong>Schváleno ředitelem</strong> → žádost jde obchodnímu referentovi</li>
              <li><strong>Přijato / Objednáno</strong> — referent zpracovává nákup</li>
              <li><strong>Dokončeno</strong> — žádost je uzavřena</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>4. Role v systému:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li><strong>Žadatel</strong> — vytváří žádosti (zaměstnanci školy)</li>
              <li><strong>Vedoucí</strong> — schvaluje / zamítá / ruší žádosti</li>
              <li><strong>Hlavní ekonom</strong> — schvaluje, zamítá nebo odkládá</li>
              <li><strong>Ředitel</strong> — schvaluje nebo zamítá</li>
              <li><strong>Obchodní referent</strong> — přijímá, objednává a dokončuje</li>
            </ul>
          </div>

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
            <strong>💡 Tip:</strong> Před vytvořením žádosti si připravte důvod nákupu, středisko a ideálně i dodavatele a částku.
            Po vytvoření dostane vybraný vedoucí e-mail s žádostí ke schválení.
          </div>
        </div>
      </DetailsModal>

      <DetailsModal
        isOpen={showPropertyHelp}
        onClose={() => setShowPropertyHelp(false)}
        title="Jak použít systém správy majetku"
      >
        <div style={{ lineHeight: '1.6' }}>
          <h3 style={{ marginTop: 0, color: '#0d9488' }}>Rychlý návod pro žadatele:</h3>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <strong>1. Vytvoření žádosti o přesun majetku:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Klikněte na "Otevřít" a vyberte "Žadatel"</li>
              <li>Klikněte na "+ Nová žádost"</li>
              <li>Vyplňte formulář:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>Zdrojová budova</strong> - odkud se majetek přemisťuje</li>
                  <li><strong>Umístění</strong> - konkrétní místo (číslo místnosti, patro)</li>
                  <li><strong>Inventární číslo</strong> - jedno nebo více čísel oddělených čárkou</li>
                  <li><strong>Cílová budova</strong> - kam se majetek přemisťuje, nebo zaškrtněte "Sklad"</li>
                  <li><strong>Cílové umístění</strong> - místo v cílové budově (není třeba při přemístění na sklad)</li>
                  <li><strong>Příjemce</strong> - <span style={{ color: '#d97706', fontWeight: 'bold' }}>DŮLEŽITÉ:</span> vyberte osobu, která bude <strong>skutečně přebírat majetek</strong>:
                    <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                      <li>Pro <strong>učební majetek</strong> - vyberte <strong>správce učeben</strong> (osoba zodpovědná za učební majetek)</li>
                      <li>Pro jiný majetek - vyberte osobu, která bude majetek fyzicky přijímat a převzít za něj odpovědnost</li>
                      <li>Při přesunu na <strong>sklad</strong> - můžete vybírat <strong>pouze z referentů majetku</strong></li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>Pro přesun na <strong>sklad</strong>: zvolte způsob dopravy (vlastní nebo organizovaná správcem budov)</li>
              <li>Klikněte "Vytvořit žádost"</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>2. Jako příjemce majetku:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Obdržíte email s informacemi o žádosti</li>
              <li>Můžete <strong>přijmout s vlastní dopravou</strong> - sami si zajistíte převoz</li>
              <li>Nebo <strong>požádat o organizaci dopravy</strong> - správce budov zajistí převoz</li>
              <li>Po fyzickém převzetí majetku klikněte na tlačítko <strong>"Potvrdit převzetí"</strong></li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <strong>3. Sledování žádosti:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li><span style={{ color: '#3b82f6', fontWeight: 'bold' }}>Nový</span> - žádost byla vytvořena</li>
              <li><span style={{ color: '#f59e0b', fontWeight: 'bold' }}>Čeká na odpověď příjemce</span> - čeká se na vaši akci</li>
              <li><span style={{ color: '#10b981', fontWeight: 'bold' }}>Dokončeno</span> - majetek byl úspěšně přemístěn</li>
            </ul>
          </div>

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
            <strong>💡 Důležité tipy:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Ujistěte se, že <strong>inventární číslo je správné</strong> - zkontrolujte fyzicky na majetku</li>
              <li>Před převzetím <strong>fyzicky zkontrolujte stav majetku</strong></li>
            </ul>
          </div>

          <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
            <strong>Potřebujete další pomoc?</strong><br/>
            Kontaktujte správce majetku nebo IT správce: <a href="mailto:vasyliev@iss-cheb.cz" style={{ color: '#0d9488' }}>vasyliev@iss-cheb.cz</a>
          </div>
        </div>
      </DetailsModal>
    </div>
  )
}

export default IntranetHome


